import { useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  SelectInput,
  TextField,
  Title,
  SimpleList,
  useListContext,
  usePermissions,
} from "react-admin";
import type { RaRecord } from "react-admin";
import { Box, Button, Tab, Tabs, Typography, useMediaQuery } from "@mui/material";
import { ChangeSadaratDialog } from "@/components/raza/ChangeSadaratDialog";
import { formatListDate, formatWeekdayFullUtc, parseDayjs } from "@/utils/date-format";
import { fromGregorian } from "@/utils/hijri-date-utils";
import {
  MISSING_SADARAT_GENDER_CHOICES,
  parseMissingSadaratGenderScope,
} from "@/utils/missing-sadarat-gender-scope";
import { hasPermission } from "@/utils/permission-utils";
import { razaEventTitle } from "@/utils/raza-event-display";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { formatRazaEventSadaratListLabel } from "@/utils/format-raza-event-sadarat-display";
import {
  missingSadaratListQuery,
  type MissingSadaratDateScope,
} from "./missing-sadarat-date-scope";

const formatEventDateUtc = (date: unknown) =>
  date ? formatListDate(String(date), { utc: true }) : "—";

const formatEventDayOfWeekUtc = (date: unknown) =>
  date ? formatWeekdayFullUtc(String(date)) : "—";

const formatEventHijriUtc = (date: unknown) =>
  date ? fromGregorian(parseDayjs(String(date), true).toDate(), "code") : "—";

function kindLabel(record: RaRecord): string {
  if (record.ohbatType) return `Ohbat · ${String(record.ohbatType)}`;
  if (record.razaRequestId) return "Raza";
  return "Event";
}

function missingSlotsLabel(r: RaRecord): string {
  return formatRazaEventSadaratListLabel({
    needSadaratMale: r.needSadaratMale,
    needSadaratFemale: r.needSadaratFemale,
    sadaratMale: r.sadaratMale as { name?: string } | null,
    sadaratFemale: r.sadaratFemale as { name?: string } | null,
  });
}

const missingSadaratFilters = [
  <SelectInput
    key="missingSadaratGender"
    source="missingSadaratGender"
    label="Sadarat gender"
    choices={[...MISSING_SADARAT_GENDER_CHOICES]}
    emptyText="All"
    alwaysOn
    sx={{ minWidth: { xs: "100%", sm: 200 } }}
  />,
];

function MissingSadaratAssignDialog({
  majlisId,
  onClose,
}: {
  majlisId: string | number;
  onClose: () => void;
}) {
  const { filterValues } = useListContext();
  const focusGender = parseMissingSadaratGenderScope(filterValues?.missingSadaratGender);
  return (
    <ChangeSadaratDialog open majlisId={majlisId} focusGender={focusGender} onClose={onClose} />
  );
}

function MissingSadaratListContent({
  canEdit,
  assignId,
  setAssignId,
}: {
  canEdit: boolean;
  assignId: string | number | null;
  setAssignId: (id: string | number | null) => void;
}) {
  const isNarrow = useMediaQuery((t) => t.breakpoints.down("md"), { noSsr: true });

  return (
    <>
      {isNarrow ? (
        <SimpleList
          primaryText={(r: RaRecord) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1.5,
                width: "100%",
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontWeight={600} sx={{ wordBreak: "break-word" }}>
                  {razaEventTitle(r) || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {formatEventDateUtc(r.date)} · {kindLabel(r)}
                  {r.startTime ? ` · ${formatRazaEventStartTimeLabel(String(r.startTime))}` : ""}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {missingSlotsLabel(r)}
                </Typography>
                {(r.hostName || r.address) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {[r.hostName, r.address].filter(Boolean).join(" · ")}
                  </Typography>
                )}
              </Box>
              {canEdit ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ flexShrink: 0, minHeight: 40, px: 1.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAssignId(r.id);
                  }}
                >
                  Assign
                </Button>
              ) : null}
            </Box>
          )}
          linkType={false}
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick={false}>
          <FunctionField
            label="Date"
            sortBy="date"
            render={(r: RaRecord) => (
              <Box>
                <Typography variant="body2">{formatEventDateUtc(r.date)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatEventDayOfWeekUtc(r.date)} · {formatEventHijriUtc(r.date)}
                </Typography>
              </Box>
            )}
          />
          <FunctionField label="Kind" render={(r: RaRecord) => kindLabel(r)} />
          <FunctionField label="Event" render={(r: RaRecord) => razaEventTitle(r) || "—"} />
          <FunctionField
            label="Time"
            render={(r: RaRecord) =>
              r.startTime ? formatRazaEventStartTimeLabel(String(r.startTime)) : "—"
            }
          />
          <FunctionField label="Sadarat" render={(r: RaRecord) => missingSlotsLabel(r)} />
          <TextField source="hostName" label="Host" emptyText="—" />
          <TextField source="address" label="Venue" emptyText="—" />
          <FunctionField
            label=""
            render={(r: RaRecord) =>
              canEdit ? (
                <Button size="small" variant="outlined" onClick={() => setAssignId(r.id)}>
                  Assign
                </Button>
              ) : null
            }
          />
        </Datagrid>
      )}
      {assignId != null ? (
        <MissingSadaratAssignDialog majlisId={assignId} onClose={() => setAssignId(null)} />
      ) : null}
    </>
  );
}

export default function RazaEventsMissingSadaratList() {
  const [dateScope, setDateScope] = useState<MissingSadaratDateScope>("upcoming");
  const { permissions } = usePermissions();
  const canEdit = hasPermission(permissions, "razaEvents.edit");
  const [assignId, setAssignId] = useState<string | number | null>(null);
  const listQuery = missingSadaratListQuery(dateScope);

  return (
    <>
      <Title title="Allocate sadarat" />
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, bgcolor: "background.paper" }}>
        <Tabs
          value={dateScope}
          onChange={(_, v: MissingSadaratDateScope) => setDateScope(v)}
          aria-label="Missing sadarat events by date"
        >
          <Tab label="Upcoming" value="upcoming" />
          <Tab label="Past" value="past" />
        </Tabs>
      </Box>
      <List
        key={dateScope}
        resource="razaEventsMissingSadarat"
        title={false}
        sort={listQuery.sort}
        perPage={50}
        filters={missingSadaratFilters}
        queryOptions={{ meta: { dateScope } }}
      >
        <MissingSadaratListContent
          canEdit={canEdit}
          assignId={assignId}
          setAssignId={setAssignId}
        />
      </List>
    </>
  );
}
